import type { Doc } from "building-types/doc";
import { docAutoDimensionAssociatedHandles } from "building-types/handles/autoDimensionHandleTools";
import type {
  AutoDimensionHandle,
  DocEntityHandle,
  LockableEntityHandle,
} from "building-types/handles/docEntityHandleTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { docEntityHandleHash } from "./core/handleHash";
import docEntityGetParent from "./docEntityGetParent";
import { docGetGroup } from "./docGetGroup";
import { docGetGroupImmediateChildren } from "./docGetGroupImmediateChildren";
import { docEntityGetGroup } from "./entityGroupTools";

export const lockableEntityTypes: { [K in LockableEntityHandle["type"]]: true } = {
  GROUP: true,
  LEVEL: true,
  UNDERLAY: true,
  STAIR_SYSTEM: true,
  STAIR_BLOCK: true,
  REFERENCE_GEOM: true,
  BLOCK: true,
  ROOM: true,
  MEASUREMENT: true,
  FOUNDATION_SYSTEM: true,
  FLOOR_SYSTEM: true,
  ROOF_SYSTEM: true,
  RAILING: true,
};

export function docEntityIsLockable(doc: Doc, handle: DocEntityHandle): handle is LockableEntityHandle {
  if (handle.type !== "GROUP") {
    return handle.type in lockableEntityTypes;
  }

  const group = docGetGroup(doc, handle);

  if (!group || group.isBuiltIn) {
    return false;
  }

  const children = docGetGroupImmediateChildren(doc, handle);
  return children.every((child) => docEntityIsLockable(doc, child));
}

export function docEntityIsLocked(doc: Doc, handle: DocEntityHandle): boolean {
  return doc.locked[docEntityHandleHash(handle)] !== undefined;
}

/**
 * True when the entity is locked because a containing GROUP or structural parent is locked.
 * Inherited locks cannot be toggled on the child; unlock the GROUP or parent instead.
 */
export function docEntityLockIsInherited(doc: Doc, handle: DocEntityHandle): boolean {
  if (!docEntityOrParentIsLocked(doc, handle)) {
    return false;
  }

  if (docEntityIsLocked(doc, handle)) {
    const parentGroup = docEntityGetGroup(doc, handle);
    if (parentGroup && docEntityOrParentIsLocked(doc, parentGroup)) {
      return true;
    }

    const parent = docEntityGetParent(doc, handle);
    if (parent && docEntityOrParentIsLocked(doc, parent)) {
      return true;
    }

    return false;
  }

  return true;
}

function docEntityIsLockedViaParentChain(doc: Doc, handle: DocEntityHandle): boolean {
  const parent = docEntityGetParent(doc, handle);
  if (!parent) {
    return false;
  }
  return docEntityOrParentIsLocked(doc, parent);
}

function docAutoDimensionOrParentIsLocked(doc: Doc, handle: AutoDimensionHandle): boolean {
  return docAutoDimensionAssociatedHandles(handle).some((associatedHandle) =>
    docEntityOrParentIsLocked(doc, associatedHandle),
  );
}

export function docEntityOrParentIsLocked(doc: Doc, handle: DocEntityHandle): boolean {
  if (docEntityIsLocked(doc, handle)) {
    return true;
  }

  // Groups are a parallel parent via entityGroups, not part of the structural parent chain.
  const parentGroup = docEntityGetGroup(doc, handle);
  if (parentGroup && docEntityOrParentIsLocked(doc, parentGroup)) {
    return true;
  }

  switch (handle.type) {
    // System entities do not walk the structural parent chain. Group membership was already
    // checked above, so a containing locked GROUP still locks these types.
    case "FLOOR_SYSTEM":
    case "FOUNDATION_SYSTEM":
    case "RAILING":
    case "ROOF_SYSTEM":
    case "REFERENCE_GEOM":
    case "STAIR_SYSTEM":
    case "UNDERLAY":
      return false;

    case "SHAPE":
    case "EDGE":
    case "VERTEX":
    case "ROOF_SIDE":
      return docEntityOrParentIsLocked(doc, handle.parent);

    case "ROOM":
    case "BLOCK":
    case "RAILING_POST":
    case "STAIR_BLOCK":
    case "FLOOR":
    case "FLOOR_FINISH":
    case "CEILING":
    case "EDGE_BEHAVIOR":
    case "OPENING":
    case "PROP_INSTANCE":
    case "CASEWORK":
    case "COLUMN":
    case "MODIFIER":
    case "EDGE_MODIFIER":
    case "MEASUREMENT":
    case "GROUP":
    case "ANNOTATION":
    case "ROOF":
    case "FOUNDATION":
    case "DORMER":
    case "ROOF_OPENING":
    case "SKYLIGHT":
    case "ROOM_LAYOUT":
    case "ROOM_LAYOUT_INSTANCE":
    case "FACADE_SPAN_STRATUM":
    case "FACADE_SWEEP_STRATUM":
    case "PROP":
    case "ELECTRICAL_DEVICE":
    case "ELECTRICAL_LAYOUT":
    case "FLOOR_BAY":
    case "FLOOR_PERIMETER_OPENING":
    case "FLOOR_SPLIT_LINE":
    case "SITE_SHAPE":
    case "CONTENT_TEMPLATE_INSTANCE":
    case "DRAWING_REF":
      return docEntityIsLockedViaParentChain(doc, handle);

    case "AUTO_DIMENSION":
      return docAutoDimensionOrParentIsLocked(doc, handle);

    case "DOC_REF":
    case "LEVEL":
    case "FACADE":
    case "ROOF_VOLUME":
    case "SHEET":
    case "OPTION":
    case "OPTION_SET":
    case "INSTANCE_OPTION_SET":
    case "OPTION_LOCK":
    case "PALETTE":
    case "SETTINGS":
    case "SETTINGS_SET":
    case "ELECTRICAL_RULE":
    case "ENTITY_SETTINGS":
    case "OPTION_EFFECT":
    case "OPTION_RULE":
    case "OPTION_TEMPLATE":
    case "OPTION_SET_OVERRIDES":
    case "PRESET":
    case "ELEVATION":
    case "SITE":
    case "VIEW_OVERRIDE":
    case "SELECTOR":
    case "VIEW_SELECTOR":
    case "VIEW_3D":
    case "ELEVATION_VIEW":
    case "PLAN_VIEW":
    case "CATALOG_ITEM":
    case "EDIT_MODE":
    case "GENERATED_ENTITY":
    case "PLAN_SET_TEMPLATE":
    case "CONTENT_TEMPLATE":
    case "CONTENT_REPLICATOR":
    case "LOCAL_PARAMETER":
    case "GLOBAL_PARAMETER":
    case "SYSTEM_PARAMETER":
    case "VIEW_TEMPLATE":
    case "VIEWPORT":
    case "DATA_TABLE":
    case "SECTION_VIEW":
    case "DATA_SEQUENCE":
    case "PLAN_SET_TEMPLATE_CONFIG":
    case "LABEL":
    case "VIEW_TEMPLATE_OVERRIDE":
    case "EFFECT":
    case "DATUM":
    case "RULE_BASED_SELECTOR":
    case "RULE_BASED_SELECTOR_INSTANCE":
    case "ASSET":
    case "PROFILE":
    case "RAILING_SETTING":
    case "INDETERMINATE_ENTITY_TYPE":
    case "FRAMING_COMPONENT":
    case "FOUNDATION_ASSEMBLY":
    case "FOUNDATION_COMPONENT":
    case "COMPONENT":
    case "COMPONENT_INSTANCE":
    case "HEADER_ASSEMBLY":
    case "FRAME_FLOOR_ASSEMBLY":
    case "SLAB_FLOOR_ASSEMBLY":
    case "SLAB_COMPONENT":
    case "SHEATHING_COMPONENT":
    case "MASONRY_COMPONENT":
    case "CIP_COMPONENT":
    case "COMPOSITE_LEVEL":
    case "REPORTABLE_UNIT_DESIGNATION":
    case "WALL_PLANE_ASSEMBLY":
    case "WALL_PLANE_FILTER":
    case "WALL_PLANE_RULE":
    case "WALL_PLANE_TRIM":
    case "FREEFORM_WALL":
    case "STAIR_SETTING":
      return false;
    default: {
      assertUnreachable(handle);
    }
  }
}
